/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useMemo } from 'react';
import { connectorTypeFilterStyles } from './power.styles';
import SelectedValue from '../selected-value/selected-value.component';
import chevronRightIcon from '../../../../../assets/chevron-right.svg';
import { Slider } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { selectLocationsParams } from '../../../../../store/locations/locations-list.selector';
import { setLocationsParamsPower } from '../../../../../store/locations/locations-list.slice';
import debounce from 'lodash.debounce';
import { NumberParam, useQueryParams } from 'use-query-params';
import {
    DEFAULT_MAX_POWER,
    marks,
    DEFAULT_MIN_POWER,
    validatePower,
    DEFAULT_INDEX_RANGE,
    DEFAULT_MAX_INDEX,
    DEFAULT_MIN_INDEX,
    indexToValue,
    valueToIndex,
} from './power.constants';
import { useIntl } from 'react-intl';

const Power = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [indexRange, setIndexRange] = useState(DEFAULT_INDEX_RANGE);
    const [query, setQuery] = useQueryParams({
        minPower: NumberParam,
        maxPower: NumberParam,
    });
    const intl = useIntl();

    const value = useMemo(
        () => [indexToValue(indexRange[0]), indexToValue(indexRange[1])],
        [indexRange]
    );

    const dispatch = useDispatch();
    const searchFilters = useSelector(selectLocationsParams);

    useEffect(() => {
        const debouncedDispatch = debounce((minPower: number, maxPower: number) => {
            dispatch(setLocationsParamsPower({ minPower, maxPower }));
        }, 500);

        debouncedDispatch(value[0], value[1]);

        return () => {
            debouncedDispatch.cancel();
        };
    }, [value, dispatch]);

    useEffect(() => {
        if (query.minPower || query.maxPower) {
            const values = validatePower({
                minPower: query.minPower,
                maxPower: query.maxPower,
            });

            if (values) {
                setIndexRange([valueToIndex(values.minPower), valueToIndex(values.maxPower)]);
                setQuery({
                    minPower: values.minPower,
                    maxPower: values.maxPower,
                });
            }
        }
    }, []);

    useEffect(() => {
        const values = validatePower({
            minPower: searchFilters.minPower,
            maxPower: searchFilters.maxPower,
        });

        if (values) {
            setQuery({
                minPower: values.minPower,
                maxPower: values.maxPower,
            });
        }
    }, [searchFilters.minPower, searchFilters.maxPower, setQuery]);

    const removeFilter = () => {
        setIndexRange(DEFAULT_INDEX_RANGE);
        dispatch(
            setLocationsParamsPower({
                minPower: DEFAULT_MIN_POWER,
                maxPower: DEFAULT_MAX_POWER,
            })
        );
    };

    const handleChange = (event: Event, newValue: number | number[]) => {
        if (Array.isArray(newValue) && newValue?.[0] === newValue?.[1]) {
            return;
        }

        setIndexRange(newValue as number[]);
    };

    return (
        <div css={connectorTypeFilterStyles.root}>
            <div css={connectorTypeFilterStyles.topBlock}>
                <div css={connectorTypeFilterStyles.headerWrapper}>
                    <span css={connectorTypeFilterStyles.title}>{intl.formatMessage({ id: 'powerFilterLabel' })}</span>
                    <SelectedValue value={`${value[0]} - ${value[1]}`} onRemove={removeFilter} />
                </div>
                <img
                    css={
                        isOpen
                            ? [
                                  connectorTypeFilterStyles.chevronIcon,
                                  connectorTypeFilterStyles.chevronIconOpen,
                              ]
                            : connectorTypeFilterStyles.chevronIcon
                    }
                    src={chevronRightIcon}
                    alt="chevron-right"
                    onClick={() => setIsOpen(!isOpen)}
                />
            </div>
            {isOpen && (
                <div css={connectorTypeFilterStyles.contentWrapper}>
                    <Slider
                        aria-labelledby="track-inverted-range-slider"
                        value={indexRange}
                        min={DEFAULT_MIN_INDEX}
                        max={DEFAULT_MAX_INDEX}
                        onChange={handleChange}
                        marks={marks}
                        step={null}
                    />
                </div>
            )}
        </div>
    );
};

export default Power;
